import { useState, useMemo, useEffect } from 'react'
import Header from './components/Header'
import WineLookup from './components/WineLookup'
import HowItWorks from './components/HowItWorks'
import WineEducation from './components/WineEducation'
import CookieConsent from './components/CookieConsent'
import PrivacyPolicy from './components/PrivacyPolicy'
import wineData from './data/wine-list.json'
import type { Vintage, WineResult } from './types/wine.types'

function App() {
  const [selectedVintage, setSelectedVintage] = useState<string>('')
  const [selectedRegion, setSelectedRegion] = useState<string>('')
  const [result, setResult] = useState<WineResult | null>(null)
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false)

  useEffect(() => {
    const handleOpenPrivacy = () => setIsPrivacyOpen(true)
    window.addEventListener('openPrivacyPolicy', handleOpenPrivacy)
    return () => window.removeEventListener('openPrivacyPolicy', handleOpenPrivacy)
  }, [])

  const vintages = useMemo(() => {
    return (wineData as Vintage[]).map(v => v.vintage).sort((a, b) => b.localeCompare(a))
  }, [])

  const regions = useMemo(() => {
    if (!selectedVintage) return []
    const vintage = (wineData as Vintage[]).find(v => v.vintage === selectedVintage)
    return vintage ? vintage.regions.map(r => r.name).sort() : []
  }, [selectedVintage])

  const handleVintageChange = (vintage: string) => {
    setSelectedVintage(vintage)
    setSelectedRegion('')
    setResult(null)
  }

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region)
    setResult(null)
  }

  const handleLookup = () => {
    if (!selectedVintage || !selectedRegion) return

    const vintage = (wineData as Vintage[]).find(v => v.vintage === selectedVintage)
    if (!vintage) return

    const region = vintage.regions.find(r => r.name === selectedRegion)
    if (!region) return

    setResult({
      vintage: selectedVintage,
      region: selectedRegion,
      description: region.description,
      quality: region.quality
    })
  }

  return (
    <div className="min-h-screen">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <WineLookup
            vintages={vintages}
            regions={regions}
            selectedVintage={selectedVintage}
            selectedRegion={selectedRegion}
            result={result}
            onVintageChange={handleVintageChange}
            onRegionChange={handleRegionChange}
            onLookup={handleLookup}
          />
          <HowItWorks />
        </div>

        <WineEducation />
      </main>

      <footer className="bg-gray-100 py-6 mt-8">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; {new Date().getFullYear()} OffVine Wine. All rights reserved.</p>
          <div className="mt-2 text-sm">
            <button
              onClick={() => setIsPrivacyOpen(true)}
              className="text-gray-500 hover:text-amber-700 underline"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      <CookieConsent />
      <PrivacyPolicy isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
    </div>
  )
}

export default App
